import { useEffect, useState } from 'react';
import { estimateRenderSec, renderCostUsd } from './cost';

// Human-readable short duration: 45s, 3m 20s, 1h 04m.
export function fmtDuration(sec) {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '–';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${String(r).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

// Estimate remaining time from a 0..100 progress value and a start timestamp
// (ms epoch). Linear extrapolation — good enough for a progress ETA.
export function progressEta(startedAt, progress, nowMs = Date.now()) {
  const elapsedSec = startedAt ? Math.max(0, (nowMs - startedAt) / 1000) : 0;
  const pct = Math.max(0, Math.min(100, progress || 0));
  if (!startedAt || pct < 1) return { elapsedSec, etaSec: null, pct };
  const totalSec = elapsedSec / (pct / 100);
  return { elapsedSec, etaSec: Math.max(0, totalSec - elapsedSec), pct };
}

// A ticking clock so ETA/elapsed labels update live. Returns Date.now() that
// re-renders every `intervalMs` while `active`.
export function useNow(active = true, intervalMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [active, intervalMs]);
  return now;
}

// Friendly label for a render/processing job, e.g. "Publishing 42% · ~3m 10s left".
// `render.etaSec` is the backend's calibrated up-front estimate (model params in
// config/grading render.etaModel, fit from real bake wallclocks) — preferred
// early on, when linear extrapolation from a few % is wildly unstable.
export function renderEtaLabel(render, nowMs = Date.now(), verb = 'Publishing') {
  if (!render || !render.status) return '';
  if (render.status === 'queued') {
    return render.etaSec ? `Queued · ~${fmtDuration(render.etaSec)} total` : 'Queued — starting shortly…';
  }
  if (render.status === 'ready') return 'Ready';
  if (render.status === 'error') return 'Failed';
  if (render.status === 'rendering') {
    const { etaSec, pct, elapsedSec } = progressEta(render.startedAt, render.progress, nowMs);
    const head = `${verb} ${pct ? `${Math.round(pct)}%` : ''}`.trim();
    const model = render.etaSec ? Math.max(0, render.etaSec - elapsedSec) : null;
    // Under 15% the extrapolation has almost no signal — lean on the model.
    if (model != null && (etaSec == null || pct < 15)) {
      return model > 0 ? `${head} · ~${fmtDuration(model)} left` : `${head} · finishing up…`;
    }
    if (etaSec == null) return `${head} · estimating… (${fmtDuration(elapsedSec)} elapsed)`;
    if (etaSec <= 0) return `${head} · finishing up…`;
    // Progress is stage-weighted, not time-uniform (the renditions tail used to
    // sit at 99% for many minutes) — once both signals exist, show the larger
    // so the countdown never collapses early and then climbs back up.
    return `${head} · ~${fmtDuration(model != null ? Math.max(etaSec, Math.min(model, etaSec * 3)) : etaSec)} left`;
  }
  return render.status;
}

// Status values that mean "a worker is actively running this job". Covers the
// various verbs different backend workers use (render/master/reframe/develop…).
const ACTIVE_JOB_STATUSES = ['rendering', 'composing', 'analyzing', 'developing', 'processing', 'exporting'];

// Generic live status label for ANY {status, progress, startedAt} job-like
// object. `estSec` is an up-front wallclock estimate used to show a sensible
// "~N left" before the worker reports enough progress to extrapolate.
export function jobEtaLabel(job, { verb = 'Working', estSec = 0, nowMs = Date.now() } = {}) {
  if (!job || !job.status) return '';
  if (job.status === 'queued') return estSec ? `Queued · ~${fmtDuration(estSec)}` : 'Queued — starting shortly…';
  if (job.status === 'ready') return 'Ready';
  if (job.status === 'error') return 'Failed';
  if (ACTIVE_JOB_STATUSES.includes(job.status)) {
    const { etaSec, pct, elapsedSec } = progressEta(job.startedAt, job.progress, nowMs);
    const head = `${verb}${pct ? ` ${Math.round(pct)}%` : ''}`;
    if (etaSec != null && etaSec > 0) return `${head} · ~${fmtDuration(etaSec)} left`;
    if (etaSec != null && etaSec <= 0) return `${head} · finishing up…`;
    // No progress to extrapolate yet — lean on the up-front estimate.
    if (estSec > 0) return `${head} · ~${fmtDuration(Math.max(0, estSec - elapsedSec))} left`;
    return `${head} · ${fmtDuration(elapsedSec)} elapsed`;
  }
  return job.status;
}

// ── Asset render jobs (stills / develop / grade) ──────────────────────────────
// These bake inline on the always-on task and stamp a `renderJob` descriptor onto
// the asset (kind/status/startedAt/etaSec/costEstUsd/costUsd). The trip thumbnail
// and the Processes page both read it. Statuses: queued | rendering | done | error.
const RENDER_VERB = { still: 'Baking still', develop: 'Developing', grade: 'Exporting', clipGrade: 'Rendering clip', upscale: 'Upscaling' };

export function renderJobActive(rj) {
  return !!rj && (rj.status === 'queued' || rj.status === 'rendering');
}

// In-flight sub-statuses each worker writes onto the asset doc. We key the live
// feedback off THESE (which the deployed backend already writes), not just the
// enriched `renderJob` — so the thumbnail + Processes page work the moment the
// frontend ships, regardless of whether the worker build stamps renderJob.
const ACTIVE_SUB = {
  stillGrab: ['queued', 'rendering'],
  gradeRender: ['queued', 'rendering'],
  develop: ['queued', 'developing'],
  clipGradeRender: ['queued', 'rendering'],
};

// Synthesize a unified render descriptor from whatever in-flight sub-status an
// asset currently has (still grab / grade export / RAW develop), enriched by the
// backend's `renderJob` when present (metered cost + title). Returns null if the
// asset isn't currently rendering.
export function assetRenderJob(asset) {
  if (!asset) return null;
  const rj = asset.renderJob || {};
  // The GPU upscale runs in its own drainer and streams live progress onto
  // `asset.upscaleJob` (stage/step/pct/label). It's a distinct phase that
  // precedes the re-bake, so surface it FIRST with its real streamed numbers.
  const uj = asset.upscaleJob;
  if (uj && (uj.status === 'rendering' || uj.status === 'queued')) {
    return {
      kind: 'upscale',
      status: uj.status === 'queued' ? 'queued' : 'rendering',
      startedAt: uj.startedAt || null,
      live: true,
      stage: uj.stage || '',
      pct: Number.isFinite(uj.pct) ? uj.pct : null,
      label: uj.label || '',
      step: uj.step, max: uj.max,
      model: uj.model, scale: uj.scale,
      etaSec: 0,
      title: rj.title || asset.name,
    };
  }
  let kind = null;
  let sub = null;
  if (ACTIVE_SUB.stillGrab.includes(asset.stillGrab?.status)) { kind = 'still'; sub = asset.stillGrab; }
  else if (ACTIVE_SUB.gradeRender.includes(asset.gradeRender?.status)) { kind = 'grade'; sub = asset.gradeRender; }
  else if (ACTIVE_SUB.develop.includes(asset.develop?.status)) { kind = 'develop'; sub = asset.develop; }
  else if (ACTIVE_SUB.clipGradeRender.includes(asset.clipGradeRender?.status)) { kind = 'clipGrade'; sub = asset.clipGradeRender; }
  else if (rj.status === 'queued' || rj.status === 'rendering') { kind = rj.kind || 'still'; sub = rj; }
  else return null;
  const status = sub.status === 'queued' ? 'queued' : 'rendering';
  const etaSec = rj.etaSec || estimateRenderSec(kind, !!(asset.lineage?.sourceIsRaw || asset.isRaw));
  return {
    kind,
    status,
    startedAt: sub.startedAt || rj.startedAt || null,
    etaSec,
    costEstUsd: rj.costEstUsd != null ? rj.costEstUsd : Math.round(renderCostUsd(etaSec) * 1e4) / 1e4,
    costUsd: rj.costUsd,
    title: rj.title || asset.name,
    sourceName: rj.sourceName || asset.lineage?.sourceName || '',
  };
}

// Live label, e.g. "Baking still · ~1m 05s left" or "Queued · ~70s".
export function renderJobLabel(rj, nowMs = Date.now()) {
  if (!rj || !rj.status) return '';
  const verb = RENDER_VERB[rj.kind] || 'Processing';
  // Live GPU upscale: drive the label off the REAL streamed stage/step/pct rather
  // than an ETA extrapolation (a diffusion restore has no reliable up-front ETA).
  if (rj.live && rj.kind === 'upscale') {
    if (rj.status === 'queued') return `${rj.scale || 4}× ${rj.model === 'supir' ? 'SUPIR' : rj.model || ''} · queued`.trim();
    const elapsed = rj.startedAt ? Math.max(0, (nowMs - rj.startedAt) / 1000) : 0;
    const head = `Upscaling ${rj.scale || 4}×`;
    const stage = rj.stage && rj.stage !== 'processing' ? ` · ${rj.stage}` : '';
    const prog = Number.isFinite(rj.pct) ? ` ${rj.pct}%`
      : (Number.isFinite(rj.step) && Number.isFinite(rj.max) ? ` ${rj.step}/${rj.max}` : '');
    return `${head}${stage}${prog} · ${fmtDuration(elapsed)}`.trim();
  }
  if (rj.status === 'queued') return rj.etaSec ? `Queued · ~${fmtDuration(rj.etaSec)}` : 'Queued — starting…';
  if (rj.status === 'done' || rj.status === 'ready') return 'Ready';
  if (rj.status === 'error') return 'Failed';
  if (rj.status === 'rendering') {
    const elapsed = rj.startedAt ? Math.max(0, (nowMs - rj.startedAt) / 1000) : 0;
    const left = rj.etaSec ? rj.etaSec - elapsed : null;
    if (left == null) return `${verb}…`;
    // The timer only ever counts DOWN. Once a frame outlasts its estimate (common
    // for RED stills under heavy concurrency), hold a steady label instead of a
    // rising elapsed — a rising number reads as a wrong/growing ETA.
    if (left <= 0) return `${verb} · wrapping up…`;
    return `${verb} · ~${fmtDuration(left)} left`;
  }
  return rj.status;
}

// 0..100 progress for a render job's bar, extrapolated from the up-front ETA.
// Linear up to 85% by the estimate, then an asymptotic creep toward 96% so an
// overrunning job (the estimate was too low) keeps inching forward instead of
// sitting frozen at the cap. Never reaches 100 until the worker flips it to done.
export function renderJobProgress(rj, nowMs = Date.now()) {
  if (!rj) return 0;
  if (rj.status === 'done' || rj.status === 'ready') return 100;
  // Live upscale reports a real % for sampler stages; other stages (tiled VAE) have
  // no clean %, so return null → the card shows an indeterminate (animated) bar.
  if (rj.live && rj.kind === 'upscale') return Number.isFinite(rj.pct) ? rj.pct : null;
  if (rj.status === 'queued') return 0;
  if (!rj.startedAt || !rj.etaSec) return 0;
  const elapsed = Math.max(0, (nowMs - rj.startedAt) / 1000);
  const eta = rj.etaSec;
  const pct = elapsed <= eta
    ? (elapsed / eta) * 85
    : 85 + 11 * (1 - Math.exp(-(elapsed - eta) / eta));
  return Math.max(0, Math.min(96, Math.round(pct)));
}

// Up-front wallclock estimates (seconds) for the editor's latent jobs. These are
// deliberately rough/conservative; the live progress ETA takes over once the
// worker starts reporting. Tuned to the backend's ~realtime composite throughput.
export function estimateMasterSec(durSec = 0) {
  if (!durSec) return 0;
  return Math.round(durSec * 1.3) + 20; // compose the flat preview master + spin-up
}
export function estimateReframeSec(durSec = 0) {
  if (!durSec) return 0;
  return Math.round(durSec * 2.2) + 30; // master compose + per-frame person detection
}
export function estimateWaveformSec(durSec = 0) {
  if (!durSec) return 15;
  return Math.round(durSec * 0.25) + 10; // decode audio track + extract peaks
}
